import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ShortCodeTag } from './ShortCodeTag.js';
import { CompanyHoverBadge } from './CompanyHoverBadge.js';

interface Option {
  value: string;
  label: string;
}

interface AsyncSearchableSelectProps {
  /** Currently selected value. */
  value: string;
  /** Called when selection changes. */
  onChange: (value: string) => void;
  /**
   * Fetch options from the server.
   * Receives the current search string; returns the matching options.
   * Called on mount with '' to load initial options, then debounced on type.
   */
  fetchOptions: (search: string) => Promise<Option[]>;
  placeholder?: string;
  disabled?: boolean;
  emptyMessage?: string;
  /** Debounce delay in ms (default 300). */
  debounceMs?: number;
  isCompanySelect?: boolean;
}

function renderSelectOptionLabel(label: string, companyId?: string, isCompanySelect = true) {
  if (!label) return null;

  // Check for [🔴 EXPIRED: ...]
  const expiredMatch = label.match(/^(.*?)\s*\[🔴\s*(EXPIRED.*?|\s*)\]$/i);
  if (expiredMatch) {
    return (
      <div className="flex items-center justify-between gap-2 w-full min-w-0">
        <span className="truncate text-red-900 font-medium">{expiredMatch[1]}</span>
        <span className="px-2 py-0.5 text-[10.5px] font-bold bg-red-100 text-red-800 border border-red-300 rounded-md shrink-0 whitespace-nowrap inline-flex items-center gap-1 shadow-2xs">
          <span className="w-1.5 h-1.5 rounded-full bg-red-600 animate-pulse"></span>
          {expiredMatch[2].trim() || 'EXPIRED'}
        </span>
      </div>
    );
  }

  // Check for [⚠️ Exp: ...] or [⚠️ Expiring: ...]
  const warningMatch = label.match(/^(.*?)\s*\[⚠️\s*(Exp.*?|\s*)\]$/i);
  if (warningMatch) {
    return (
      <div className="flex items-center justify-between gap-2 w-full min-w-0">
        <span className="truncate text-amber-950 font-medium">{warningMatch[1]}</span>
        <span className="px-2 py-0.5 text-[10.5px] font-bold bg-amber-100 text-amber-900 border border-amber-300 rounded-md shrink-0 whitespace-nowrap inline-flex items-center gap-1 shadow-2xs">
          <span>⚠️</span>
          <span>{warningMatch[2].trim() || 'Expiring Soon'}</span>
        </span>
      </div>
    );
  }

  const match = label.match(/^(.*?)\s*\[([A-Z0-9_-]+)\]$/i);
  if (match) {
    const mainText = match[1];
    const code = match[2];
    return (
      <div className="flex items-center justify-between gap-2 w-full min-w-0">
        <span className="truncate">{mainText}</span>
        {isCompanySelect ? (
          <CompanyHoverBadge shortCode={code} companyId={companyId}>
            <ShortCodeTag code={code} showIcon={false} />
          </CompanyHoverBadge>
        ) : (
          <span className="px-2 py-0.5 text-[11px] font-mono font-bold bg-indigo-50 text-indigo-700 border border-indigo-200/80 rounded-md shrink-0">
            {code}
          </span>
        )}
      </div>
    );
  }
  return <span className="truncate">{label}</span>;
}

/**
 * A searchable dropdown that fetches options from the server on each
 * keystroke (debounced). Suitable for large datasets where client-side
 * filtering would be too slow. Renders options via React Portal with fixed positioning
 * so it never gets clipped by table/modal overflow.
 */
export function AsyncSearchableSelect({
  value,
  onChange,
  fetchOptions,
  placeholder = 'Select…',
  disabled = false,
  emptyMessage = 'No results found',
  debounceMs = 300,
  isCompanySelect = true,
}: AsyncSearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [options, setOptions] = useState<Option[]>([]);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [selectedLabel, setSelectedLabel] = useState('');
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

  // Position calculation for React Portal
  const updatePosition = useCallback(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const dropHeight = 240;
      const spaceBelow = window.innerHeight - rect.bottom;

      if (spaceBelow < dropHeight && rect.top > dropHeight) {
        setDropdownStyle({
          position: 'fixed',
          top: Math.max(10, rect.top - dropHeight - 4),
          left: rect.left,
          width: rect.width,
          zIndex: 99999,
        });
      } else {
        setDropdownStyle({
          position: 'fixed',
          top: rect.bottom + 4,
          left: rect.left,
          width: rect.width,
          zIndex: 99999,
        });
      }
    }
  }, []);

  // When value changes, resolve label from options.
  useEffect(() => {
    const match = options.find((o) => o.value === value);
    if (match) {
      setSelectedLabel(match.label);
    } else if (value) {
      fetchOptions('').then((initial) => {
        const m = initial.find((o) => o.value === value);
        if (m) {
          setSelectedLabel(m.label);
          setOptions((prev) => {
            const ids = new Set(prev.map((p) => p.value));
            return ids.has(m.value) ? prev : [...prev, m];
          });
        }
      }).catch(() => { /* ignore */ });
    } else {
      setSelectedLabel('');
    }
  }, [value, options, fetchOptions]);

  // Fetch options (debounced).
  const doFetch = useCallback(
    (q: string) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      setLoading(true);
      debounceRef.current = setTimeout(async () => {
        try {
          const opts = await fetchOptions(q);
          setOptions(opts);
        } catch { /* ignore */ }
        setLoading(false);
      }, debounceMs);
    },
    [fetchOptions, debounceMs],
  );

  // Load initial options when dropdown opens & attach portal scroll listeners
  useEffect(() => {
    if (open) {
      setSearch('');
      doFetch('');
      updatePosition();
      requestAnimationFrame(() => inputRef.current?.focus());

      const handleScroll = () => updatePosition();
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleScroll);
      return () => {
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleScroll);
        if (debounceRef.current) clearTimeout(debounceRef.current);
      };
    }
  }, [open, doFetch, updatePosition]);

  // Re-fetch on search change.
  useEffect(() => {
    if (open) doFetch(search);
  }, [search, open, doFetch]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        containerRef.current &&
        !containerRef.current.contains(target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleSelect = useCallback(
    (val: string) => {
      onChange(val);
      setOpen(false);
    },
    [onChange],
  );

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onChange('');
      setSelectedLabel('');
    },
    [onChange],
  );

  const filtered = useMemo(() => options, [options]);

  return (
    <div ref={containerRef} className="relative">
      {/* Trigger */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => { if (!disabled) setOpen((v) => !v); }}
        className={`input h-10 text-left flex items-center justify-between gap-2 w-full ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
      >
        <div className={`w-full min-w-0 truncate ${selectedLabel ? 'text-gray-900 font-medium' : 'text-gray-400'}`}>
          {selectedLabel ? renderSelectOptionLabel(selectedLabel, value, isCompanySelect) : <span className="truncate block">{placeholder}</span>}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {value && !disabled && (
            <span
              onClick={handleClear}
              className="text-gray-400 hover:text-gray-600 p-0.5 rounded hover:bg-gray-100"
            >
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
          )}
          <svg
            className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}
            viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
          >
            <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      </button>

      {/* Dropdown Menu rendered via React Portal to prevent table clipping */}
      {open &&
        createPortal(
          <div
            ref={dropdownRef}
            style={dropdownStyle}
            className="bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden text-xs"
          >
            {/* Search input */}
            <div className="p-2 border-b border-gray-100 bg-slate-50">
              <input
                ref={inputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search company name or short code…"
                className="w-full px-2 py-1 text-xs bg-white border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
                onKeyDown={(e) => {
                  if (e.key === 'Escape') setOpen(false);
                  if (e.key === 'Enter' && filtered.length === 1) handleSelect(filtered[0].value);
                }}
              />
            </div>

            {/* Options list */}
            <ul className="max-h-52 overflow-y-auto py-1 divide-y divide-slate-50">
              {loading ? (
                <li className="px-3 py-2 text-xs text-gray-400 text-center">
                  <span className="inline-block animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-brand-500 mr-2" />
                  Loading companies…
                </li>
              ) : filtered.length === 0 ? (
                <li className="px-3 py-2 text-xs text-gray-400 text-center">{emptyMessage}</li>
              ) : (
                filtered.map((opt) => (
                  <li key={opt.value}>
                    <button
                      type="button"
                      onClick={() => handleSelect(opt.value)}
                      className={`w-full text-left px-3 py-2 text-xs transition-colors ${
                        opt.value === value
                          ? 'bg-brand-50 text-brand-700 font-medium'
                          : 'text-gray-700 hover:bg-slate-50'
                      }`}
                    >
                      {renderSelectOptionLabel(opt.label, opt.value, isCompanySelect)}
                    </button>
                  </li>
                ))
              )}
            </ul>
          </div>,
          document.body
        )}
    </div>
  );
}
